/**
 * Membership promotion banner — reusable across the site.
 *
 * Design reminder: Syne-led display type, electric-magenta invitation signal,
 * gold accent on the node that represents the seat being bought. The layout is
 * deliberately airy: copy occupies a little over half the width, the vector
 * lattice sits right, and the gutter between them carries the white space.
 *
 * `variant="bare"` drops the outer section padding so the banner can sit
 * inside another surface (for example a rotating slide) without doubling up
 * spacing. Styles are emitted once per mount, matching the global <style>
 * convention used by the pages in this codebase.
 */

import { Link } from "wouter";
import { ArrowRight, CalendarDays } from "lucide-react";
import { useOptionalAuth } from "@/contexts/AuthContext";
import { MEMBERSHIP_JOIN_CTA, MEMBERSHIP_PUBLIC_PRICE_COPY } from "@shared/membershipPaidTiers";

const MEMBERSHIP_HREF = "/membership";

/** Abstract node lattice: the network you join, with your seat lit in gold. */
function MembershipLattice() {
  return (
    <svg
      className="membership-banner__art"
      viewBox="0 0 260 200"
      role="img"
      aria-label="Abstract lattice of connected members with one seat highlighted"
    >
      <defs>
        <radialGradient id="chi-membership-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#E91E8C" stopOpacity=".38" />
          <stop offset="60%" stopColor="#E91E8C" stopOpacity=".08" />
          <stop offset="100%" stopColor="#E91E8C" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="chi-membership-edge" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E91E8C" stopOpacity=".85" />
          <stop offset="100%" stopColor="#DBA514" stopOpacity=".55" />
        </linearGradient>
      </defs>

      <circle cx="164" cy="96" r="86" fill="url(#chi-membership-glow)" />

      <g stroke="url(#chi-membership-edge)" strokeWidth="1" fill="none" opacity=".62">
        <path d="M164 96 L72 44" />
        <path d="M164 96 L58 118" />
        <path d="M164 96 L106 170" />
        <path d="M164 96 L232 40" />
        <path d="M164 96 L242 138" />
        <path d="M164 96 L188 178" />
        <path d="M72 44 L232 40" opacity=".4" />
        <path d="M58 118 L106 170" opacity=".4" />
        <path d="M242 138 L188 178" opacity=".4" />
      </g>

      <g fill="#3DB658" opacity=".9">
        <circle cx="72" cy="44" r="3.4" />
        <circle cx="106" cy="170" r="3.4" />
      </g>
      <g fill="#E91E8C" opacity=".95">
        <circle cx="58" cy="118" r="3.4" />
        <circle cx="232" cy="40" r="3.4" />
        <circle cx="242" cy="138" r="3.4" />
        <circle cx="188" cy="178" r="3.4" />
      </g>

      {/* The member seat */}
      <circle cx="164" cy="96" r="21" fill="none" stroke="#DBA514" strokeWidth="1.1" opacity=".55" />
      <circle cx="164" cy="96" r="13" fill="none" stroke="#DBA514" strokeWidth="1.6" opacity=".9" />
      <circle cx="164" cy="96" r="6" fill="#DBA514" />
    </svg>
  );
}

export type MembershipBannerProps = {
  /** "bare" removes the outer section padding for embedding inside another surface. */
  variant?: "section" | "bare";
  /** Overrides for pages that want to angle the pitch differently. */
  eyebrow?: string;
  headingLead?: string;
  headingAccent?: string;
  copy?: string;
  ctaLabel?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
};

export default function MembershipBanner({
  variant = "section",
  eyebrow = "CHI Alliance Membership",
  headingLead = "Join the room",
  headingAccent = "where it happens.",
  copy = MEMBERSHIP_PUBLIC_PRICE_COPY,
  ctaLabel = MEMBERSHIP_JOIN_CTA,
  secondaryLabel = "Explore events",
  secondaryHref = "/calendar-of-events",
}: MembershipBannerProps) {
  // Never sell a membership to someone who already has one. When auth state is
  // unavailable the visitor is treated as signed out, so the pitch still shows.
  const auth = useOptionalAuth();
  if (auth?.isPremium) return null;

  const inner = (
    <div className={`membership-banner__inner${variant === "bare" ? " membership-banner__inner--bare" : ""}`}>
      <div className="membership-banner__aura" aria-hidden="true" />

      <div className="membership-banner__content">
        <p className="membership-banner__eyebrow">{eyebrow}</p>
        <h2 className="membership-banner__heading">
          {headingLead} <span>{headingAccent}</span>
        </h2>
        <p className="membership-banner__copy">{copy}</p>

        <div className="membership-banner__actions chi-cta-pair">
          <Link href={MEMBERSHIP_HREF} className="membership-banner__button chi-btn-primary">
            <span>{ctaLabel}</span>
            <ArrowRight size={16} aria-hidden="true" className="membership-banner__button-arrow" />
          </Link>
          <Link href={secondaryHref} className="membership-banner__button chi-btn-outline">
            <CalendarDays size={15} aria-hidden="true" />
            <span>{secondaryLabel}</span>
          </Link>
        </div>
      </div>

      <div className="membership-banner__visual" aria-hidden="false">
        <MembershipLattice />
      </div>
    </div>
  );

  return (
    <>
      {variant === "section" ? (
        <section className="membership-banner">
          <div className="container">{inner}</div>
        </section>
      ) : (
        inner
      )}

      <style>{`
        .membership-banner { padding: clamp(4rem, 7vw, 6.5rem) 0; background: linear-gradient(120deg, #000 0%, #0B0716 55%, #000 100%); }
        .membership-banner__inner { position: relative; isolation: isolate; display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, .95fr); align-items: center; gap: clamp(2rem, 6vw, 5.5rem); overflow: hidden; padding: clamp(2.25rem, 4.5vw, 3.9rem); border: 1px solid rgba(168,85,247,.28); border-radius: 1.5rem; background: radial-gradient(circle at 84% 16%, rgba(0,212,255,.1), transparent 31%), linear-gradient(126deg, rgba(102,119,255,.1), rgba(255,255,255,.025) 42%, rgba(233,30,140,.12)); box-shadow: inset 0 1px 0 rgba(255,255,255,.07), 0 28px 80px rgba(0,0,0,.3); }
        .membership-banner__inner--bare { background-color: #09070D; background-image: linear-gradient(90deg, rgba(6,7,15,.98) 0%, rgba(6,7,15,.91) 35%, rgba(6,7,15,.56) 64%, rgba(6,7,15,.2) 100%), linear-gradient(180deg, rgba(6,7,15,.08), rgba(6,7,15,.72)), url('/media/events/membership-happy-hour.webp'); background-position: center, center, 74% center; background-repeat: no-repeat; background-size: cover; }
        .membership-banner__aura { position: absolute; z-index: -1; width: 24rem; height: 24rem; right: -8rem; top: -10rem; border-radius: 999px; opacity: .66; pointer-events: none; background: conic-gradient(from 210deg, rgba(0,212,255,.24), rgba(102,119,255,.22), rgba(233,30,140,.2), rgba(219,165,20,.16), rgba(0,212,255,.24)); filter: blur(32px); }
        .membership-banner__content { position: relative; z-index: 1; max-width: 30rem; }
        .membership-banner__eyebrow { margin: 0; color: #F5A623; font-family: 'Space Grotesk', sans-serif; font-size: .66rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
        .membership-banner__heading { margin: .8rem 0 0; color: #FFFFFF; font-family: 'Syne', 'Space Grotesk', sans-serif; font-size: clamp(2rem, 4.2vw, 3.5rem); font-weight: 700; letter-spacing: -.075em; line-height: .98; }
        .membership-banner__heading span { background: linear-gradient(102deg, #E91E8C 8%, #DBA514 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .membership-banner__copy { max-width: 27rem; margin: .95rem 0 0; color: #A8B4C8; font-family: 'Space Grotesk', sans-serif; font-size: .92rem; line-height: 1.7; }
        .membership-banner__actions { margin-top: 1.9rem; }
        .membership-banner__button { display: inline-flex; align-items: center; justify-content: center; gap: .62rem; padding: .84rem 1.35rem; border-radius: .42rem; font-family: 'Space Grotesk', sans-serif; font-size: .76rem; font-weight: 700; letter-spacing: .04em; text-decoration: none; cursor: pointer; }
        .membership-banner__button-arrow { transition: transform 180ms ease; }
        .membership-banner__button:hover .membership-banner__button-arrow, .membership-banner__button:focus-visible .membership-banner__button-arrow { transform: translateX(3px); }
        .membership-banner__visual { position: relative; z-index: 1; display: flex; align-items: center; justify-content: center; }
        .membership-banner__art { width: 100%; max-width: 19rem; height: auto; overflow: visible; }

        @media (max-width: 900px) {
          .membership-banner__inner { grid-template-columns: 1fr; gap: 2.25rem; }
          .membership-banner__inner--bare { background-position: center, center, 76% center; }
          .membership-banner__content { max-width: none; }
          .membership-banner__visual { order: -1; justify-content: flex-start; }
          .membership-banner__art { max-width: 13rem; }
        }

        @media (max-width: 560px) {
          .membership-banner__inner { padding: 1.6rem; }
          .membership-banner__inner--bare { background-position: center, center, 74% center; }
        }

        @media (prefers-reduced-motion: reduce) {
          .membership-banner__button, .membership-banner__button-arrow { transition: none !important; }
        }
      `}</style>
    </>
  );
}
